/**
 * Physical dimensions as exponent vectors over the seven SI base quantities.
 *
 * A dimension is what survives when you strip the prefix and the choice of
 * unit: volts and millivolts share one dimension, and that is exactly the
 * question "is this expression adding a length to a time" needs answered.
 * Magnitude is tracked separately as a scale factor (see `Unit`), because this
 * tool never converts between units — it evaluates the numbers you typed — so
 * adding millimetres to metres is a real defect that a dimension check alone
 * would wave through.
 */

/** Index order for every exponent vector in this module. */
export const BASE_DIMENSIONS = [
  'length',
  'mass',
  'time',
  'current',
  'temperature',
  'amount',
  'luminous',
] as const;

/** SI symbols, parallel to `BASE_DIMENSIONS`, for rendering. */
const BASE_SYMBOLS = ['m', 'kg', 's', 'A', 'K', 'mol', 'cd'] as const;

/** Exponents over `BASE_DIMENSIONS`. Fractional entries are legal — sqrt halves them. */
export type Dimension = readonly number[];

export const DIMENSIONLESS: Dimension = [0, 0, 0, 0, 0, 0, 0];

export function dim(
  length = 0,
  mass = 0,
  time = 0,
  current = 0,
  temperature = 0,
  amount = 0,
  luminous = 0,
): Dimension {
  return [length, mass, time, current, temperature, amount, luminous];
}

export function dimMul(a: Dimension, b: Dimension): Dimension {
  return a.map((v, i) => v + b[i]);
}

export function dimDiv(a: Dimension, b: Dimension): Dimension {
  return a.map((v, i) => v - b[i]);
}

export function dimPow(a: Dimension, n: number): Dimension {
  return a.map((v) => v * n);
}

/**
 * Exponents are compared with a tolerance because `sqrt` and `^0.5` produce
 * halves that will not survive a chain of arithmetic exactly.
 */
export function dimEqual(a: Dimension, b: Dimension): boolean {
  return a.every((v, i) => Math.abs(v - b[i]) < 1e-9);
}

export function isDimensionless(a: Dimension): boolean {
  return dimEqual(a, DIMENSIONLESS);
}

const SUPERSCRIPT: Record<string, string> = {
  '-': '⁻',
  '.': '·',
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

function superscript(n: number): string {
  if (n === 1) return '';
  return String(n)
    .split('')
    .map((c) => SUPERSCRIPT[c] ?? c)
    .join('');
}

/**
 * Render a dimension in SI base symbols, e.g. `kg·m²·s⁻³·A⁻¹`.
 *
 * Callers should prefer `describeDimension` from the registry, which names the
 * derived unit (`V`) when one exists and falls back to this.
 */
export function formatDimension(d: Dimension): string {
  if (isDimensionless(d)) return 'dimensionless';
  const parts: string[] = [];
  for (let i = 0; i < d.length; i++) {
    if (Math.abs(d[i]) < 1e-9) continue;
    parts.push(`${BASE_SYMBOLS[i]}${superscript(d[i])}`);
  }
  return parts.join('·');
}
